import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Users, RefreshCw, Loader2, User, Shield } from 'lucide-react';

const UsersTab = ({ agentId }) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserData();
  }, [agentId]);

  const fetchUserData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/agents/${agentId}/users`);
      setUserData(res.data);
    } catch (error) {
      toast.error('Failed to load user information');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">User Information</h3>
        <Button onClick={fetchUserData} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Current User Card */}
      {userData?.current_user && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Current User
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Username</p>
                <p className="font-medium">{userData.current_user.username}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Domain/Workgroup</p>
                <p className="font-medium">{userData.current_user.domain}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">State</p>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  userData.current_user.state === 'Active' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {userData.current_user.state}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Last Logged Users Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Last 5 Logged Users
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium">Username</th>
                  <th className="text-left py-3 px-4 font-medium">Domain</th>
                  <th className="text-left py-3 px-4 font-medium">Login Time</th>
                  <th className="text-left py-3 px-4 font-medium">Security Group</th>
                </tr>
              </thead>
              <tbody>
                {!userData?.last_logged_users || userData.last_logged_users.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center py-8 text-gray-500">
                      No user login history available
                    </td>
                  </tr>
                ) : (
                  userData.last_logged_users.map((user, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium">{user.username}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{user.domain || 'N/A'}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {user.login_time ? new Date(user.login_time).toLocaleString() : 'N/A'}
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          <Shield className="w-3 h-3" />
                          {user.security_group || 'Users'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UsersTab;
